// Accessibility utilities for WCAG compliance

/**
 * Keyboard navigation handler
 * @param {Object} options - Navigation options
 * @param {Function} options.onEnter - Handler for Enter key
 * @param {Function} options.onSpace - Handler for Space key
 * @param {Function} options.onEscape - Handler for Escape key
 * @param {Function} options.onArrowUp - Handler for Arrow Up key
 * @param {Function} options.onArrowDown - Handler for Arrow Down key
 * @param {Function} options.onArrowLeft - Handler for Arrow Left key
 * @param {Function} options.onArrowRight - Handler for Arrow Right key
 * @param {Function} options.onTab - Handler for Tab key
 */
export const useKeyboardNavigation = (options = {}) => {
  const handleKeyDown = (event) => {
    const { key } = event;
    
    switch (key) {
      case 'Enter':
        if (options.onEnter) {
          event.preventDefault();
          options.onEnter(event);
        }
        break;
      case ' ':
        if (options.onSpace) {
          event.preventDefault();
          options.onSpace(event);
        }
        break;
      case 'Escape':
        if (options.onEscape) {
          event.preventDefault();
          options.onEscape(event);
        }
        break;
      case 'ArrowUp':
        if (options.onArrowUp) {
          event.preventDefault();
          options.onArrowUp(event);
        }
        break;
      case 'ArrowDown':
        if (options.onArrowDown) {
          event.preventDefault();
          options.onArrowDown(event);
        }
        break;
      case 'ArrowLeft':
        if (options.onArrowLeft) {
          event.preventDefault();
          options.onArrowLeft(event);
        }
        break;
      case 'ArrowRight':
        if (options.onArrowRight) {
          event.preventDefault();
          options.onArrowRight(event);
        }
        break;
      case 'Tab':
        if (options.onTab) {
          // Don't prevent default for Tab to maintain natural flow
          options.onTab(event);
        }
        break;
      default:
        break;
    }
  };

  return handleKeyDown;
};

/**
 * Focus management utilities
 */
export const focusManager = {
  /**
   * Get all focusable elements within a container
   * @param {HTMLElement} container - Container element
   * @returns {HTMLElement[]} Array of focusable elements
   */
  getFocusableElements: (container) => {
    if (!container) return [];
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');
    
    return Array.from(container.querySelectorAll(focusableSelectors))
      .filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && 
               style.visibility !== 'hidden' && 
               !el.hasAttribute('aria-hidden');
      });
  },

  /**
   * Trap focus within a container (for modals, dialogs)
   * @param {HTMLElement} container - Container to trap focus within
   * @param {boolean} active - Whether focus trap is active
   */
  trapFocus: (container, active = true) => {
    if (!container) return () => {};

    const focusableElements = focusManager.getFocusableElements(container);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (event) => {
      if (!active || event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);

    // Focus first element initially
    if (active && firstElement) {
      firstElement.focus();
    }

    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  },

  /**
   * Save and restore focus
   * @returns {Object} Object with save and restore methods
   */
  createFocusSaver: () => {
    let savedElement = null;

    return {
      save: () => {
        savedElement = document.activeElement;
      },
      restore: () => {
        if (savedElement && savedElement.focus) {
          savedElement.focus();
        }
      }
    };
  }
};

/**
 * ARIA utilities
 */
export const ariaUtils = {
  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   * @param {string} priority - Priority level ('polite' or 'assertive')
   */
  announce: (message, priority = 'polite') => {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.setAttribute('class', 'sr-only');
    announcer.textContent = message;
    
    document.body.appendChild(announcer);
    
    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  },

  /**
   * Generate unique ID for aria relationships
   * @param {string} prefix - Prefix for the ID
   * @returns {string} Unique ID
   */
  generateId: (prefix = 'aria') => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Set up aria-describedby relationship
   * @param {HTMLElement} element - Element to describe
   * @param {HTMLElement} description - Description element
   */
  describe: (element, description) => {
    if (!element || !description) return;
    
    let id = description.id;
    if (!id) {
      id = ariaUtils.generateId('desc');
      description.id = id;
    }
    
    const existingDescribedBy = element.getAttribute('aria-describedby');
    if (existingDescribedBy) {
      element.setAttribute('aria-describedby', `${existingDescribedBy} ${id}`);
    } else {
      element.setAttribute('aria-describedby', id);
    }
  },

  /**
   * Set up aria-labelledby relationship
   * @param {HTMLElement} element - Element to label
   * @param {HTMLElement} label - Label element
   */
  labelBy: (element, label) => {
    if (!element || !label) return;
    
    let id = label.id;
    if (!id) {
      id = ariaUtils.generateId('label');
      label.id = id;
    }
    
    element.setAttribute('aria-labelledby', id);
  }
};

/**
 * Color contrast checker
 * @param {string} foreground - Foreground color (hex)
 * @param {string} background - Background color (hex)
 * @returns {Object} Contrast information
 */
export const checkColorContrast = (foreground, background) => {
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Calculate relative luminance
  const getLuminance = (rgb) => {
    const { r, g, b } = rgb;
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);

  if (!fg || !bg) return null;

  const l1 = getLuminance(fg);
  const l2 = getLuminance(bg);

  const contrast = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);

  return {
    ratio: Math.round(contrast * 100) / 100,
    aaCompliant: contrast >= 4.5,
    aaaCompliant: contrast >= 7,
    level: contrast >= 7 ? 'AAA' : contrast >= 4.5 ? 'AA' : 'Fail'
  };
};

/**
 * Screen reader utilities
 */
export const screenReaderUtils = {
  /**
   * Create screen reader only text
   * @param {string} text - Text for screen readers
   * @returns {HTMLElement} Hidden element with text
   */
  createSROnlyText: (text) => {
    const element = document.createElement('span');
    element.className = 'sr-only';
    element.textContent = text;
    return element;
  },

  /**
   * Check if screen reader is likely active
   * @returns {boolean} Whether screen reader might be active
   */
  isScreenReaderActive: () => {
    // Check for common screen reader indicators
    return !!(
      window.speechSynthesis ||
      navigator.userAgent.includes('NVDA') ||
      navigator.userAgent.includes('JAWS') ||
      navigator.userAgent.includes('VoiceOver')
    );
  }
};

/**
 * Reduced motion utilities
 */
export const motionUtils = {
  /**
   * Check if user prefers reduced motion
   * @returns {boolean} Whether reduced motion is preferred
   */
  prefersReducedMotion: () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  /**
   * Apply animation only if motion is not reduced
   * @param {Function} animationFn - Animation function to call
   * @param {Function} fallbackFn - Fallback for reduced motion
   */
  safeAnimate: (animationFn, fallbackFn = null) => {
    if (motionUtils.prefersReducedMotion()) {
      fallbackFn && fallbackFn();
    } else {
      animationFn();
    }
  }
};

/**
 * High contrast mode detection
 */
export const contrastUtils = {
  /**
   * Check if high contrast mode is active
   * @returns {boolean} Whether high contrast mode is active
   */
  isHighContrast: () => {
    return window.matchMedia('(prefers-contrast: high)').matches;
  },

  /**
   * Apply high contrast styles conditionally
   * @param {HTMLElement} element - Element to style
   * @param {Object} styles - High contrast styles
   */
  applyHighContrastStyles: (element, styles) => {
    if (contrastUtils.isHighContrast() && element) {
      Object.assign(element.style, styles);
    }
  }
};

/**
 * Keyboard shortcuts manager
 */
export const keyboardShortcuts = {
  shortcuts: new Map(),

  /**
   * Register keyboard shortcut
   * @param {string} combination - Key combination (e.g., 'ctrl+s', 'alt+enter')
   * @param {Function} handler - Handler function
   * @param {Object} options - Options
   */
  register: (combination, handler, options = {}) => {
    const normalizedCombo = combination.toLowerCase().replace(/\s/g, '');
    keyboardShortcuts.shortcuts.set(normalizedCombo, { handler, options });
  },

  /**
   * Unregister keyboard shortcut
   * @param {string} combination - Key combination
   */
  unregister: (combination) => {
    const normalizedCombo = combination.toLowerCase().replace(/\s/g, '');
    keyboardShortcuts.shortcuts.delete(normalizedCombo);
  },

  /**
   * Handle keyboard event
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeyboardEvent: (event) => {
    const { key, ctrlKey, altKey, shiftKey, metaKey } = event;
    
    let combo = '';
    if (ctrlKey) combo += 'ctrl+';
    if (altKey) combo += 'alt+';
    if (shiftKey) combo += 'shift+';
    if (metaKey) combo += 'meta+';
    combo += key.toLowerCase();

    const shortcut = keyboardShortcuts.shortcuts.get(combo);
    if (shortcut) {
      const { handler, options } = shortcut;
      
      // Check if shortcut should be prevented
      if (options.preventDefault !== false) {
        event.preventDefault();
      }
      
      // Check if shortcut should stop propagation
      if (options.stopPropagation) {
        event.stopPropagation();
      }
      
      handler(event);
    }
  },

  /**
   * Initialize keyboard shortcuts listener
   */
  init: () => {
    document.addEventListener('keydown', keyboardShortcuts.handleKeyboardEvent);
  },

  /**
   * Cleanup keyboard shortcuts listener
   */
  cleanup: () => {
    document.removeEventListener('keydown', keyboardShortcuts.handleKeyboardEvent);
    keyboardShortcuts.shortcuts.clear();
  }
};

/**
 * Form accessibility helpers
 */
export const formAccessibility = {
  /**
   * Add proper form validation messages
   * @param {HTMLElement} field - Form field
   * @param {string} message - Validation message
   * @param {string} type - Message type ('error', 'success', 'info')
   */
  addValidationMessage: (field, message, type = 'error') => {
    if (!field) return;

    // Remove existing message
    const existingMessage = field.parentNode.querySelector('.validation-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create new message
    const messageElement = document.createElement('div');
    messageElement.className = `validation-message validation-${type}`;
    messageElement.textContent = message;
    messageElement.setAttribute('role', type === 'error' ? 'alert' : 'status');
    messageElement.setAttribute('aria-live', 'polite');

    // Add unique ID and associate with field
    const messageId = ariaUtils.generateId('validation');
    messageElement.id = messageId;
    
    // Update field's aria-describedby
    ariaUtils.describe(field, messageElement);

    // Insert message after field
    field.parentNode.insertBefore(messageElement, field.nextSibling);

    // Set field's invalid state
    if (type === 'error') {
      field.setAttribute('aria-invalid', 'true');
    } else {
      field.setAttribute('aria-invalid', 'false');
    }
  },

  /**
   * Clear validation messages for a field
   * @param {HTMLElement} field - Form field
   */
  clearValidationMessage: (field) => {
    if (!field) return;

    const message = field.parentNode.querySelector('.validation-message');
    if (message) {
      message.remove();
    }
    
    field.setAttribute('aria-invalid', 'false');
  }
};

const accessibilityUtils = {
  useKeyboardNavigation,
  focusManager,
  ariaUtils,
  checkColorContrast,
  screenReaderUtils,
  motionUtils,
  contrastUtils,
  keyboardShortcuts,
  formAccessibility
};

export default accessibilityUtils;